import { useState } from "react";
import { BlockProps } from "../../../types";
import { Button, cn } from "@tellescope/design-system";
import { Textarea } from "@tellescope/ui";
import { Flag } from "lucide-react";
import { useStateWithFallback } from "@tellescope/react-hooks";
import { useTranslation } from "react-i18next";
import { useBlock } from "../../../hooks";
import { BlockHeader } from "../BlockHeader";
import { BlockContent } from "../BlockContent";
import { BlockEmptyState } from "../BlockEmptyState";

export const NotesBlock = ({
  enduserId,
  info,
  className,
  viewId,
  blockId,
  blockIndex,
  onMaximize,
  editMode: providedEditMode,
  setEditMode: providedSetEditMode,
  alwaysShowButtons = false,
  contentClassName,
  blockLayout: providedBlockLayout,
}: BlockProps) => {
  const { t } = useTranslation("contacts");

  const {
    parsedInfo,
    isEditingTitle,
    setIsEditingTitle,
    newTitle,
    setNewTitle,
    enduserQuery: { data: noteInfo },
    editMode,
    setEditMode,
    updateInfo,
    duplicateBlock,
    deleteBlock,
    toggleExpanded,
    expanded,
    blockLayout,
    canEditTitle,
    canCreateBlock,
    canDeleteBlock,
    updateEnduserMutation,
    canEditEnduser,
  } = useBlock({
    type: "information.notes",
    info,
    editMode: providedEditMode ?? !onMaximize,
    setEditMode: providedSetEditMode,
    enduserId,
    viewId,
    blockId,
    blockIndex,
    blockLayout: providedBlockLayout,
    select: (enduser) => ({ note: enduser.note ?? "", noteIsFlagged: enduser.noteIsFlagged ?? false }),
  });

  const note = noteInfo?.note ?? "";
  const noteIsFlagged = noteInfo?.noteIsFlagged ?? false;

  const [draft, setDraft] = useStateWithFallback<string | undefined>({
    fallbackValue: note,
    fallbackCondition: (value) => value === undefined,
  });

  // When the block itself is in edit mode (fullscreen/single-block view), the note starts in the
  // textarea; blur collapses it to the read-only display without leaving the block's edit mode.
  const [isEditingNote, setIsEditingNote] = useState(editMode);

  const isEditing = isEditingNote && editMode && canEditEnduser;

  const resetDraft = () => setDraft(undefined);

  const saveNote = () => {
    setIsEditingNote(false);
    updateEnduserMutation.mutateAsync({ id: enduserId, note: draft ?? "" }, { onSuccess: resetDraft });
  };

  const toggleNoteFlag = () => {
    updateEnduserMutation.mutateAsync({ id: enduserId, noteIsFlagged: !noteIsFlagged });
  };

  return (
    <div className={cn("group", className)}>
      <BlockHeader
        editMode={editMode}
        setEditMode={setEditMode}
        isEditingTitle={isEditingTitle}
        setIsEditingTitle={setIsEditingTitle}
        newTitle={newTitle}
        setNewTitle={setNewTitle}
        canEditTitle={canEditTitle}
        parsedInfo={parsedInfo}
        updateInfo={updateInfo}
        duplicateBlock={duplicateBlock}
        deleteBlock={deleteBlock}
        canCreateBlock={canCreateBlock}
        canDeleteBlock={canDeleteBlock}
        onMaximize={onMaximize}
        alwaysShowButtons={alwaysShowButtons}
        actions={
          canEditEnduser && (
            <Button
              iconOnly
              variant="ghost"
              size="sm"
              color="muted"
              hoverLabel={noteIsFlagged ? t("Unflag note") : t("Flag note")}
              data-block-action-pinned={noteIsFlagged || undefined}
              onClick={toggleNoteFlag}
            >
              <Flag className={cn(noteIsFlagged && "fill-current text-yellow-600 dark:text-yellow-500")} />
            </Button>
          )
        }
      />
      <BlockContent
        editMode={editMode}
        setEditMode={canEditEnduser ? setEditMode : undefined}
        expanded={expanded}
        layout={blockLayout}
        onToggleExpanded={() => toggleExpanded()}
        className={cn("flex whitespace-pre-wrap text-left", contentClassName)}
      >
        {isEditing ? (
          <div className="w-full space-y-1">
            <Textarea
              autoFocus
              className="min-h-0 resize-none"
              placeholder={t("No note")}
              style={{ fieldSizing: "content" }}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  saveNote();
                }
                if (e.key === "Escape") {
                  resetDraft();
                  setIsEditingNote(false);
                }
              }}
              onBlur={saveNote}
            />
            <p className="text-xs text-muted-foreground font-light">{t("Enter to save, Esc to revert")}</p>
          </div>
        ) : editMode && canEditEnduser ? (
          <Button
            variant="ghost"
            className={cn(
              "w-full self-start whitespace-pre-wrap text-left h-auto",
              note ? "text-sm font-light justify-start py-1" : "flex-1 p-0 hover:bg-transparent"
            )}
            onClick={() => setIsEditingNote(true)}
          >
            {note || <BlockEmptyState className="hover:border-foreground/30">{t("No note")}</BlockEmptyState>}
          </Button>
        ) : note ? (
          <span className="text-sm font-light">{note}</span>
        ) : (
          <div className="flex flex-1 items-center justify-center">
            <BlockEmptyState>{t("No note")}</BlockEmptyState>
          </div>
        )}
      </BlockContent>
    </div>
  );
};
